import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ordo/ui/table'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { useServerDataGrid } from 'hooks/useServerDataGrid'
import { Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { Column } from 'types/data-grid'
import { cn } from 'utils/cn'

import { MobileCardList, MobileSortSelect } from './MobileList'
import { ColumnFilter, HeaderLabel, PaginationButton, SkeletonRows, tableMinWidth } from './shared'

export interface ServerDataGridProps<T> {
  /** List endpoint returning `{ data, total, hasMore }` — e.g. `/api/contacts`. */
  endpoint: string
  /** Extra query params merged into every request (page-level filter toggles, etc.). */
  params?: Record<string, unknown>
  columns: Column<T>[]
  keyField: keyof T & string
  /** Unique nuqs namespace so sort/filter/page/pageSize/q survive reload. */
  storageKey?: string
  pageSizeOptions?: number[]
  defaultPageSize?: number
  searchPlaceholder?: string
  emptyMessage?: string
  onRowClick?: (row: T) => void
}

const SEARCH_DEBOUNCE_MS = 200

export function ServerDataGrid<T>({
  endpoint,
  params,
  columns,
  keyField,
  storageKey,
  pageSizeOptions,
  defaultPageSize,
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results',
  onRowClick,
}: ServerDataGridProps<T>) {
  const {
    rows,
    total,
    hasMore,
    page,
    totalPages,
    setPage,
    pageSize,
    setPageSize,
    pageSizeOptions: resolvedPageSizes,
    sort,
    toggleSort,
    setSort,
    filters,
    updateFilter,
    clearFilter,
    q,
    setQ,
    loading,
  } = useServerDataGrid<T>({ endpoint, params, storageKey, pageSizeOptions, defaultPageSize })

  // Debounce the search box so each keystroke doesn't re-fetch.
  const [localSearch, setLocalSearch] = useState(q)
  const initialQRef = useRef(q)
  useEffect(() => {
    // Sync inbound URL changes (back/forward, reload) into the input.
    if (q !== initialQRef.current) {
      setLocalSearch(q)
      initialQRef.current = q
    }
  }, [q])
  useEffect(() => {
    if (localSearch === q) {
      return
    }
    const t = setTimeout(() => setQ(localSearch), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [localSearch, q, setQ])

  const hasFilters = columns.some((c) => c.filterable)
  const cell = (col: Column<T>, row: T) => {
    const raw = (row as Record<string, unknown>)[col.key]
    return col.render ? col.render(row) : typeof raw === 'string' || typeof raw === 'number' ? raw : ''
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder={searchPlaceholder}
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="h-10 pl-8 text-sm md:h-9"
          />
        </div>
        <div className="md:hidden">
          <MobileSortSelect columns={columns} sort={sort} setSort={setSort} />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border/50 text-sm text-foreground shadow-md shadow-black/5 dark:border-transparent dark:shadow-black/30">
        <div className="md:hidden">
          <MobileCardList
            columns={columns}
            rows={rows}
            keyField={keyField}
            cell={cell}
            onRowClick={onRowClick}
            loading={loading && rows.length === 0}
            emptyMessage={emptyMessage}
          />
        </div>
        <div className="hidden md:block">
          <Table className="table-fixed" style={{ minWidth: tableMinWidth(columns) }}>
            <colgroup>
              {columns.map((col) => (
                <col key={col.key} style={col.width ? { width: col.width } : undefined} />
              ))}
            </colgroup>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {columns.map((col) => (
                  <TableHead
                    key={col.key}
                    onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                    className={cn(
                      'overflow-hidden whitespace-nowrap bg-muted/30 px-3.5 py-2.5 text-[13px] font-semibold',
                      col.sortable && 'cursor-pointer select-none',
                    )}
                    style={{ textAlign: (col as Column<unknown>).align ?? 'left' }}>
                    <HeaderLabel header={col.header} column={col.key} sortable={col.sortable} sort={sort} />
                  </TableHead>
                ))}
              </TableRow>
              {hasFilters && (
                <TableRow className="hover:bg-transparent">
                  {columns.map((col) => (
                    <TableHead key={col.key} className="overflow-hidden bg-muted/30 px-3.5 py-1 font-normal">
                      {col.filterable ? (
                        <ColumnFilter
                          header={col.header}
                          filter={filters[col.key]}
                          onUpdate={(op, val) => updateFilter(col.key, op, val)}
                          onClear={() => clearFilter(col.key)}
                        />
                      ) : null}
                    </TableHead>
                  ))}
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              <LoadingWrapper
                loading={loading && rows.length === 0}
                loadingElement={<SkeletonRows columns={columns} />}>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="py-8 text-center text-muted-foreground">
                      {emptyMessage}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={String((row as Record<string, unknown>)[keyField])}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      onKeyDown={
                        onRowClick
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                onRowClick(row)
                              }
                            }
                          : undefined
                      }
                      tabIndex={onRowClick ? 0 : undefined}
                      className={onRowClick ? 'cursor-pointer' : undefined}>
                      {columns.map((col) => {
                        const raw = (row as Record<string, unknown>)[col.key]
                        return (
                          <TableCell
                            key={col.key}
                            className="truncate px-3.5 py-2.5"
                            style={{ textAlign: (col as Column<unknown>).align ?? 'left' }}>
                            {col.render
                              ? col.render(row)
                              : typeof raw === 'string' || typeof raw === 'number'
                                ? raw
                                : ''}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))
                )}
              </LoadingWrapper>
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/50 bg-muted/30 px-3.5 py-2.5 text-[13px] text-muted-foreground">
          <span>
            {total} {total === 1 ? 'row' : 'rows'}
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="h-10 w-[72px] text-[13px] md:h-7">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {resolvedPageSizes.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span>per page</span>
            </div>
            <div className="flex items-center gap-1.5">
              <PaginationButton onClick={() => setPage(0)} disabled={page === 0}>
                {'«'}
              </PaginationButton>
              <PaginationButton onClick={() => setPage(page - 1)} disabled={page === 0}>
                {'‹'}
              </PaginationButton>
              <span className="px-2">
                Page {page + 1} / {totalPages}
              </span>
              <PaginationButton onClick={() => setPage(page + 1)} disabled={!hasMore}>
                {'›'}
              </PaginationButton>
              <PaginationButton onClick={() => setPage(totalPages - 1)} disabled={!hasMore}>
                {'»'}
              </PaginationButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
